// src/routes/AppRoutes.jsx

import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../Contexts/AuthContext";

import MainLayout from "../layouts/MainLayout";
import ModuleGuard from "../Components/ModuleGuard";

// Lazy-loaded page components for dynamic code splitting
const Login = lazy(() => import("../Pages/base/Login"));
const Dashboard = lazy(() => import("../Pages/base/Dashboard"));
const Logout = lazy(() => import("../Pages/base/Logout"));
const Forbidden = lazy(() => import("../Pages/base/Forbidden"));
const ChangePassword = lazy(() => import("../Pages/base/ChangePassword"));
const Roles = lazy(() => import("../Pages/users/Roles"));
const AdminIndustries = lazy(() => import("../Pages/organizations/AdminIndustries"));
const AdminOrganizations = lazy(() => import("../Pages/organizations/AdminOrganizations"));
const AdminUsers = lazy(() => import("../Pages/users/AdminUsers"));
const ModuleManagement = lazy(() => import("../Pages/organizations/ModuleManagement"));
const OrganizationDetails = lazy(() => import("../Pages/organizations/OrganizationDetails"));
const OrganizationModules = lazy(() => import("../Pages/organizations/OrganizationModules"));
const ComingSoon = lazy(() => import("../Pages/base/ComingSoon"));
const Profile = lazy(() => import("../Pages/base/Profile"));
const Onboarding = lazy(() => import("../Pages/base/Onboarding"));
const Attendance = lazy(() => import("../Pages/Attendance"));
const LiveChat = lazy(() => import("../Pages/chat/LiveChat"));
const Vault = lazy(() => import("../Pages/vault/Vault"));
const Clients = lazy(() => import("../Pages/clients/Clients"));
const Socialmedia = lazy(() => import("../Pages/social/Socialmedia"));
const LeadsKanban = lazy(() => import("../Pages/leads/LeadsKanban"));
const AgenciesManager = lazy(() => import("../Pages/leads/AgenciesManager"));
const AgencyPortal = lazy(() => import("../Pages/leads/AgencyPortal"));
const ReceiptAccessPage = lazy(() => import("../Pages/ngo/ReceiptAccessPage"));
const CorporateTeams = lazy(() => import("../Pages/corporate/CorporateTeams"));
const CorporateTimesheets = lazy(() => import("../Pages/corporate/CorporateTimesheets"));
const CorporateLeadsTracker = lazy(() => import("../Pages/corporate/CorporateLeadsTracker"));
const NGOLeadsTracker = lazy(() => import("../Pages/ngo/NGOLeadsTracker"));
const CallsLogs = lazy(() => import("../Pages/calls/CallsLogs"));
const BillingInvoiceSystem = lazy(() => import("../Pages/billing/BillingInvoiceSystem"));
const CalendarPage = lazy(() => import("../Pages/calendar/CalendarPage"));
const Notebook = lazy(() => import("../Pages/notebook/Notebook"));
const EmployeeOnboarding = lazy(() => import("../Pages/hr/EmployeeOnboarding"));
const OnboardingSetup = lazy(() => import("../Pages/hr/OnboardingSetup"));
const OnboardingLedger = lazy(() => import("../Pages/hr/OnboardingLedger"));

// Newly ported HR, Tasks, Projects, and Policies pages
const Tasks = lazy(() => import("../Pages/tasks/Tasks"));
const Projects = lazy(() => import("../Pages/projects/Projects"));
const Leave = lazy(() => import("../Pages/hr/Leave"));
const Payroll = lazy(() => import("../Pages/hr/Payroll"));
const Policies = lazy(() => import("../Pages/organizations/Policies"));
const HRDashboard = lazy(() => import("../Pages/hr/HRDashboard"));

// Newly created Education App Modules
const EnquiryAdmission = lazy(() => import("../Pages/education/EnquiryAdmission"));
const AcademicLeadsTracker = lazy(() => import("../Pages/education/AcademicLeadsTracker"));
const Students = lazy(() => import("../Pages/education/Students"));
const EmployeesFaculty = lazy(() => import("../Pages/education/EmployeesFaculty"));
const FeeManagement = lazy(() => import("../Pages/education/FeeManagement"));
const Academics = lazy(() => import("../Pages/education/Academics"));
const Timetable = lazy(() => import("../Pages/education/Timetable"));
const EducationAttendance = lazy(() => import("../Pages/education/Attendance"));
const Examination = lazy(() => import("../Pages/education/Examination"));
const Library = lazy(() => import("../Pages/education/Library"));
const Transport = lazy(() => import("../Pages/education/Transport"));
const Hostel = lazy(() => import("../Pages/education/Hostel"));
const Communication = lazy(() => import("../Pages/education/Communication"));
const ReportsMIS = lazy(() => import("../Pages/education/ReportsMIS"));
const SettingsRoles = lazy(() => import("../Pages/education/SettingsRoles"));
const Assignments = lazy(() => import("../Pages/education/Assignments"));

// Consolidated modular ERP pages
const Inventory = lazy(() => import("../Pages/inventory/Inventory"));
const Products = lazy(() => import("../Pages/inventory/Products"));
const DemoUnits = lazy(() => import("../Pages/inventory/DemoUnits"));
const Orders = lazy(() => import("../Pages/orders/Orders"));
const PurchaseOrders = lazy(() => import("../Pages/orders/PurchaseOrders"));
const Vendors = lazy(() => import("../Pages/vendors/Vendors"));

// NGO CRM Module Pages
const NGOBeneficiaries   = lazy(() => import("../Pages/ngo/BeneficiariesPage"));
const NGOVolunteers      = lazy(() => import("../Pages/ngo/VolunteersPage"));
const NGOCampaigns       = lazy(() => import("../Pages/ngo/CampaignsPage"));
const NGOActivities      = lazy(() => import("../Pages/ngo/ActivitiesPage"));
const NGODonors          = lazy(() => import("../Pages/ngo/Donormanagement"));
const NGOExpenses        = lazy(() => import("../Pages/ngo/Expensemanagement"));
const NGOInventory       = lazy(() => import("../Pages/ngo/Stockmanagement"));
const NGOCertificates    = lazy(() => import("../Pages/ngo/Certificatemanagement"));
const NGOGPSTracking     = lazy(() => import("../Pages/ngo/Gpstracking"));
const NGOTickets         = lazy(() => import("../Pages/ngo/Ticketmanagement"));
const NGOReports         = lazy(() => import("../Pages/ngo/ReportsPage"));
const NGOWorkLogs        = lazy(() => import("../Pages/ngo/WorkLogsPage"));
const MetaAdsDashboard   = lazy(() => import("../Pages/meta_ads/MetaAdsDashboard"));
const WhatsAppSettings  = lazy(() => import("../Pages/whatsapp/WhatsAppSettings"));
const GoogleAdsDashboard = lazy(() => import("../Pages/google_ads/GoogleAdsDashboard"));

// CRM & AccountsPro CRM Module Pages
const Accounting         = lazy(() => import("../Pages/accounting/Accounting"));
const Tax                = lazy(() => import("../Pages/tax/Tax"));
const Reports            = lazy(() => import("../Pages/reports/Reports"));
const Performance        = lazy(() => import("../Pages/performance/Performance"));

// Meetings Module
const MeetingsDashboard  = lazy(() => import("../Pages/meetings/MeetingsDashboard"));
const MeetingRoom        = lazy(() => import("../Pages/meetings/MeetingRoom"));

function DynamicRouteLoader() {
  return (
    <div className="dynamic-loading-container">
      <div className="dynamic-loader-glass">
        <div className="dynamic-spinner" />
        <div className="dynamic-loading-text">Synchronizing Terminal...</div>
        <div className="dynamic-loading-sub">Downloading workspace assets</div>
      </div>
    </div>
  );
}

function FallbackRoute() {
  const { user } = useAuth();
  const target = user?.user_type === 'agency' ? '/agency-portal' : '/dashboard';
  return <Navigate to={target} replace />;
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<DynamicRouteLoader />}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/employee-onboarding" element={<EmployeeOnboarding />} />
        <Route path="/forbidden" element={<Forbidden />} />
        <Route path="/agency-portal" element={<AgencyPortal />} />
        <Route path="/receipt-access/:token" element={<ReceiptAccessPage />} />
        <Route path="/meetings/room/:code" element={<MeetingRoom />} />
        
        <Route element={<MainLayout />}>
          {/* Core Workspace - General Access */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/attendance" element={<Attendance />} />
          <Route path="/meetings" element={<ModuleGuard requiredPath="/meetings"><MeetingsDashboard /></ModuleGuard>} />
          <Route path="/chat" element={<ModuleGuard requiredPath="/chat"><LiveChat /></ModuleGuard>} />
          <Route path="/vault" element={<ModuleGuard requiredPath="/vault"><Vault /></ModuleGuard>} />
          <Route path="/clients" element={<ModuleGuard requiredPath="/clients"><Clients /></ModuleGuard>} />
          <Route path="/social" element={<ModuleGuard requiredPath="/social"><Socialmedia /></ModuleGuard>} />
          <Route path="/meta-ads" element={<ModuleGuard requiredPath="/meta-ads"><MetaAdsDashboard /></ModuleGuard>} />
          <Route path="/whatsapp-settings" element={<ModuleGuard requiredPath="/whatsapp-settings"><WhatsAppSettings /></ModuleGuard>} />
          <Route path="/google-ads" element={<ModuleGuard requiredPath="/google-ads"><GoogleAdsDashboard /></ModuleGuard>} />
          <Route path="/leads" element={<ModuleGuard requiredPath="/leads"><LeadsKanban /></ModuleGuard>} />
          <Route path="/leads/agencies" element={<ModuleGuard requiredPath="/leads/agencies"><AgenciesManager /></ModuleGuard>} />
          <Route path="/corporate-leads" element={<ModuleGuard requiredPath="/corporate-leads"><CorporateLeadsTracker /></ModuleGuard>} />
          <Route path="/corporate-teams" element={<ModuleGuard requiredPath="/corporate-teams"><CorporateTeams /></ModuleGuard>} />
          <Route path="/corporate/timesheets" element={<ModuleGuard requiredPath="/corporate/timesheets"><CorporateTimesheets /></ModuleGuard>} />
          <Route path="/calls" element={<ModuleGuard requiredPath="/calls"><CallsLogs /></ModuleGuard>} />
          <Route path="/billing" element={<ModuleGuard requiredPath="/billing"><BillingInvoiceSystem /></ModuleGuard>} />
          <Route path="/calendar" element={<ModuleGuard requiredPath="/calendar"><CalendarPage /></ModuleGuard>} />
          <Route path="/notebook" element={<ModuleGuard requiredPath="/notebook"><Notebook /></ModuleGuard>} />
          <Route path="/onboarding-setup" element={<ModuleGuard requiredPath="/onboarding-setup"><OnboardingSetup /></ModuleGuard>} />
          <Route path="/onboarding-ledger" element={<ModuleGuard requiredPath="/onboarding-ledger"><OnboardingLedger /></ModuleGuard>} />
          
          {/* AccountsPro CRM Modules */}
          <Route path="/accounting" element={<ModuleGuard requiredPath="/accounting"><Accounting /></ModuleGuard>} />
          <Route path="/tax" element={<ModuleGuard requiredPath="/tax"><Tax /></ModuleGuard>} />
          <Route path="/crm-reports" element={<ModuleGuard requiredPath="/crm-reports"><Reports /></ModuleGuard>} />
          <Route path="/performance" element={<ModuleGuard requiredPath="/performance"><Performance /></ModuleGuard>} />
          
          {/* Ported Core Modules */}
          <Route path="/tasks" element={<ModuleGuard requiredPath="/tasks"><Tasks /></ModuleGuard>} />

          <Route path="/projects" element={<ModuleGuard requiredPath="/projects"><Projects /></ModuleGuard>} />
          <Route path="/leaves" element={<ModuleGuard requiredPath="/leaves"><Leave /></ModuleGuard>} />
          <Route path="/leave" element={<ModuleGuard requiredPath="/leaves"><Leave /></ModuleGuard>} />
          <Route path="/payroll" element={<ModuleGuard requiredPath="/payroll"><Payroll /></ModuleGuard>} />
          <Route path="/policies" element={<ModuleGuard requiredPath="/policies"><Policies /></ModuleGuard>} />
          <Route path="/hr" element={<ModuleGuard requiredPath="/hr"><HRDashboard /></ModuleGuard>} />
          
          {/* Consolidated Modular ERP Routes */}
          <Route path="/inventory" element={<ModuleGuard requiredPath="/inventory"><Inventory /></ModuleGuard>} />
          <Route path="/products" element={<ModuleGuard requiredPath="/products"><Products /></ModuleGuard>} />
          <Route path="/demo-units" element={<ModuleGuard requiredPath="/demo-units"><DemoUnits /></ModuleGuard>} />
          <Route path="/orders" element={<ModuleGuard requiredPath="/orders"><Orders /></ModuleGuard>} />
          <Route path="/purchase-orders" element={<ModuleGuard requiredPath="/purchase-orders"><PurchaseOrders /></ModuleGuard>} />
          <Route path="/vendors" element={<ModuleGuard requiredPath="/vendors"><Vendors /></ModuleGuard>} />
          
          {/* Education App Modules */}
          <Route path="/education/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/education/enquiry" element={<ModuleGuard requiredPath="/education/enquiry"><EnquiryAdmission /></ModuleGuard>} />
          <Route path="/education/leads-tracker" element={<ModuleGuard requiredPath="/education/enquiry"><AcademicLeadsTracker /></ModuleGuard>} />
          <Route path="/education/students" element={<ModuleGuard requiredPath="/education/students"><Students /></ModuleGuard>} />
          <Route path="/education/employees" element={<ModuleGuard requiredPath="/education/employees"><EmployeesFaculty /></ModuleGuard>} />
          <Route path="/education/fees" element={<ModuleGuard requiredPath="/education/fees"><FeeManagement /></ModuleGuard>} />
          <Route path="/education/academics" element={<ModuleGuard requiredPath="/education/academics"><Academics /></ModuleGuard>} />
          <Route path="/education/assignments" element={<ModuleGuard requiredPath="/education/assignments"><Assignments /></ModuleGuard>} />
          <Route path="/education/timetable" element={<ModuleGuard requiredPath="/education/timetable"><Timetable /></ModuleGuard>} />
          <Route path="/education/attendance" element={<ModuleGuard requiredPath="/education/attendance"><EducationAttendance /></ModuleGuard>} />
          <Route path="/education/examination" element={<ModuleGuard requiredPath="/education/examination"><Examination /></ModuleGuard>} />
          <Route path="/education/library" element={<ModuleGuard requiredPath="/education/library"><Library /></ModuleGuard>} />
          <Route path="/education/transport" element={<ModuleGuard requiredPath="/education/transport"><Transport /></ModuleGuard>} />
          <Route path="/education/hostel" element={<ModuleGuard requiredPath="/education/hostel"><Hostel /></ModuleGuard>} />
          <Route path="/education/communication" element={<ModuleGuard requiredPath="/education/communication"><Communication /></ModuleGuard>} />
          <Route path="/education/reports" element={<ModuleGuard requiredPath="/education/reports"><ReportsMIS /></ModuleGuard>} />
          <Route path="/education/settings" element={<ModuleGuard requiredPath="/education/settings"><SettingsRoles /></ModuleGuard>} />
          
          {/* NGO CRM Module Routes */}
          <Route path="/ngo/dashboard"    element={<Navigate to="/dashboard" replace />} />
          <Route path="/ngo/beneficiaries" element={<ModuleGuard requiredPath="/ngo/beneficiaries"><NGOBeneficiaries /></ModuleGuard>} />
          <Route path="/ngo/volunteers"   element={<ModuleGuard requiredPath="/ngo/volunteers"><NGOVolunteers /></ModuleGuard>} />
          <Route path="/ngo/campaigns"    element={<ModuleGuard requiredPath="/ngo/campaigns"><NGOCampaigns /></ModuleGuard>} />
          <Route path="/ngo/activities"   element={<ModuleGuard requiredPath="/ngo/activities"><NGOActivities /></ModuleGuard>} />
          <Route path="/ngo/work-logs"    element={<ModuleGuard requiredPath="/ngo/activities"><NGOWorkLogs /></ModuleGuard>} />
          <Route path="/ngo/donors"       element={<ModuleGuard requiredPath="/ngo/donors"><NGODonors /></ModuleGuard>} />
          <Route path="/ngo/expenses"     element={<ModuleGuard requiredPath="/ngo/expenses"><NGOExpenses /></ModuleGuard>} />
          <Route path="/ngo/inventory"    element={<ModuleGuard requiredPath="/ngo/inventory"><NGOInventory /></ModuleGuard>} />
          <Route path="/ngo/certificates" element={<ModuleGuard requiredPath="/ngo/certificates"><NGOCertificates /></ModuleGuard>} />
          <Route path="/ngo/gps-tracking" element={<ModuleGuard requiredPath="/ngo/gps-tracking"><NGOGPSTracking /></ModuleGuard>} />
          <Route path="/ngo/tickets"      element={<ModuleGuard requiredPath="/ngo/tickets"><NGOTickets /></ModuleGuard>} />
          <Route path="/ngo/reports"      element={<ModuleGuard requiredPath="/ngo/reports"><NGOReports /></ModuleGuard>} />
          <Route path="/ngo/leads-tracker" element={<ModuleGuard requiredPath="/ngo/leads-tracker"><NGOLeadsTracker /></ModuleGuard>} />

          {/* RBAC & Users */}
          <Route path="/roles" element={<ModuleGuard requiredPath="/roles"><Roles /></ModuleGuard>} />
          <Route path="/users" element={<ModuleGuard requiredPath="/users"><AdminUsers /></ModuleGuard>} />
          
          {/* Organizations & Workspaces */}
          <Route path="/industries" element={<ModuleGuard requiredPath="/industries"><AdminIndustries /></ModuleGuard>} />
          <Route path="/organizations" element={<ModuleGuard requiredPath="/organizations"><AdminOrganizations /></ModuleGuard>} />
          <Route path="/organizations/:orgId" element={<ModuleGuard requiredPath="/organizations"><OrganizationModules /></ModuleGuard>} />
          <Route path="/organization-profile" element={<ModuleGuard requiredPath="/organization-profile"><OrganizationDetails /></ModuleGuard>} />
          <Route path="/modules-config" element={<ModuleGuard requiredPath="/modules-config"><ModuleManagement /></ModuleGuard>} />
          
          {/* Catch-all for Coming Soon */}
          <Route path="/coming-soon" element={<ComingSoon />} />
        </Route>
        
        {/* Fallback */}
        <Route path="*" element={<FallbackRoute />} />
      </Routes>
    </Suspense>
  );
}
